<template>
  <main>
    <v-toolbar style="background-color: #444; color: #fff; border-radius: 5px">
      <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
    </v-toolbar>
    <v-form @submit.prevent="handleSubmit">
      <formWrapper :validator="$v.form">
        <v-container>
          <v-row>
            <v-col cols="12">
              <locale-select v-if="isEdit" v-model="locale" @change="changeLocale" />
            </v-col>
            <v-col cols="12">
              <form-group name="main_media" attribute="main_media">
                <template slot-scope="{ attrs, listeners }">
                  <image-upload dense :label="$t('main_media')" v-bind="attrs" v-on="listeners" :form="form"
                    fieldName="main_media" :maxSize="maxSize" />
                </template>
              </form-group>
            </v-col>
            <v-col cols="12">
              <section class="file-upload__gallery pa-0">
                <image-upload dense :label="$t('labels.media')" :form="form" fieldName="media" :maxSize="maxSize"
                  multiple />
              </section>
            </v-col>
            <v-col cols="12">
              <first-step :countries="countries" :tourismCities="tourismCities" :isEdit="isEdit" :form="form"
                :categories="categories" :tags="tags" />
            </v-col>
            <v-col cols="12" v-if="isEdit">
              <second-step :countries="countries" :tourismCities="tourismCities" :startPointStates="startPointStates"
                :endPointState="endPointState" :startPointCites="startPointCites" :endPointCities="endPointCities"
                :isEdit="isEdit" :form="address" :categories="categories" :tags="tags" />
            </v-col>
            <v-col cols="12" v-if="isEdit">
              <third-step :isEdit="isEdit" :form="form" />
            </v-col>
            <v-col cols="12">
              <form-group name="description" attribute="fields.description">
                <template slot-scope="{ attrs, listeners }">
                  <VueEditor :placeholder="$t('fields.description')" class="editor" v-model="form.description"
                    v-on="listeners" v-bind="attrs" :editorToolbar="customToolbar" />
                </template>
              </form-group>
            </v-col>

            <v-col cols="12">
              <v-btn :disabled="$v.form.$invalid" :loading="loadingButton" type="submit" class="primary" large block
                rounded>{{ $t("button.save") }}
                <v-icon>mdi-content-save</v-icon></v-btn>
            </v-col>
            <!-- :disabled="$v.form.$invalid" -->
          </v-row>
        </v-container>
      </formWrapper>
    </v-form>
  </main>
</template>

<script>
import { VueEditor } from "vue2-editor";
import {
  required,
  minLength,
  maxLength,
  minValue,
  maxValue
} from "vuelidate/lib/validators";
import { maxWords } from "@/utils/validate";
import {
  IndexData,
  StoreData,
  ShowData,
  UpdateData
} from "@/helpers/apiMethods";

export default {
  name: "CreateAndEditJob",
  components: {
    FirstStep: () => import("./first_step"),
    SecondStep: () => import("./second_step.vue"),
    ThirdStep: () => import("./third_step.vue"),
    VueEditor
  },
  provide() {
    return {
      $v: this.$v
    };
  },
  data() {
    return {
      customToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ align: "" }, { align: "justify" }, { align: "right" }],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ direction: "rtl" }]
      ],
      maxSize: 1.24,
      loadingButton: false,
      locale: this.locale,
      form: {
        country: "",
        name: "",
        sub_name: "",
        main_media: "",
        description: "",
        label: "",
        duration_in_nights: "",
        cancel_before_start_in_hours: "",
        reserve_before_start_in_hours: "",
        return_pct: "",
        tourism_cities: [],
        tags: [],
        media: [],
        categories: [],
        conditions: [],
        excludes: [],
        includes: [],
        needs: [],
        available_dates: [],
        auto_accept_order: 0,
        allow_custom_dates: 0,
        premium: 0,
        available: 0,
        visible: 0
      },
      address: {
        start_point_address: {
          city_id: "",
          state_id: "",
          country_id: "",
          address_line_one: "",
          address_line_two: "",
          postal_code: ""
        },
        end_point_address: {
          city_id: "",
          state_id: "",
          country_id: "",
          address_line_one: "",
          address_line_two: "",
          postal_code: ""
        }
      },
      tags: [],

      startPointStates: [],
      endPointState: [],
      startPointCites: [],
      endPointCities: [],
      categories: [],
      countries: [],
      tourismCities: []
    };
  },
  computed: {
    formTitle() {
      if (this.$route.name === "create-package") {
        return this.$t("heading.create_package");
      } else {
        return this.$t("heading.edit_package");
      }
    },
    isEdit() {
      if (this.$route.name === "create-package") {
        return false;
      } else {
        return true;
      }
    }
  },
  mounted() {
    this.getCountries();
    this.getCategories();
    this.getTags();
    this.getTourismCities();
  },
  methods: {
    async getTourismCities() {
      const res = await IndexData({
        reqName: "tourism-cities",
        query: { pagination: "all" }
      });
      const { tourism_cities } = res.data;
      this.tourismCities = tourism_cities;
    },
    async getCategories() {
      const res = await IndexData({
        reqName: "package-categories?visible=yes",
        query: { pagination: "all" }
      });
      const { package_categories } = res.data;
      this.categories = package_categories;
    },
    async getTags() {
      const res = await IndexData({ reqName: "tags", pagination: "all" });
      const { tags } = res.data;
      this.tags = tags;
    },

    async getCountries() {
      const res = await IndexData({
        reqName: "countries",
        query: { pagination: "all", simple: true }
        // query: { pagination: "all" }
      });
      const { countries } = res.data;
      this.countries = countries;
    },

    handleShowData(locale = this.locale) {
      ShowData({
        reqName: "packages",
        param: this.$route.params.slug,
        locale
      }).then(res => {
        const packageData = res.data.package;
        this.form = {
          name: packageData.name,
          sub_name: packageData.sub_name,
          label: packageData.label,
          description: packageData.description,
          cancel_before_start_in_hours:
            packageData.cancel_before_start_in_hours,
          duration_in_nights: packageData.duration_in_nights,
          duration_in_nights: packageData.duration_in_nights,
          return_pct: packageData.return_pct,
          reserve_before_start_in_hours:
            packageData.reserve_before_start_in_hours,
          tourism_cities: packageData.tourism_cities.map(city => city.id),
          tags: packageData.tags.map(tag => tag.id),
          categories: packageData.categories.map(category => category.id),
          country: packageData.country.id,
          main_media: packageData.main_media,
          media: packageData.media,
          allow_custom_dates: packageData.allow_custom_dates ? "1" : "0",
          auto_accept_order: packageData.auto_accept_order ? "1" : "0",
          premium: packageData.premium ? "1" : "0",
          available: packageData.available ? "1" : "0",
          visible: packageData.visible ? "1" : "0",
          needs: packageData.needs.map(need => need.id),
          excludes: packageData.excludes.map(exclude => exclude.id),
          conditions: packageData.conditions.map(condition => condition.id),
          includes: packageData.includes.map(include => include.id),
          available_dates: packageData.available_dates.map(available_date =>
            this.$moment(available_date.available_at).format("YYYY-MM-DD")
          )
        };
        this.address = {
          start_point_address: {
            ...this.address.start_point_address,
            city_id:
              packageData.start_point_address &&
              packageData.start_point_address.city.id,
            state_id:
              packageData.start_point_address &&
              packageData.start_point_address.state.id,
            address_line_one:
              packageData.start_point_address &&
              packageData.start_point_address.address_line_one,
            address_line_two:
              packageData.start_point_address &&
              packageData.start_point_address.address_line_two,
            postal_code:
              packageData.start_point_address &&
              packageData.start_point_address.postal_code,
            country_id:
              packageData.start_point_address &&
              packageData.start_point_address.country.id
          },
          end_point_address: {
            ...this.address.end_point_address,
            city_id:
              packageData.end_point_address &&
              packageData.end_point_address.city.id,
            state_id:
              packageData.end_point_address &&
              packageData.end_point_address.state.id,
            address_line_one:
              packageData.end_point_address &&
              packageData.end_point_address.address_line_one,
            address_line_two:
              packageData.end_point_address &&
              packageData.end_point_address.address_line_two,
            postal_code:
              packageData.end_point_address &&
              packageData.end_point_address.postal_code,
            country_id:
              packageData.end_point_address &&
              packageData.end_point_address.country.id
          }
        };
      });
    },
    generateData() {
      const formData = new FormData();

      this.form.media.forEach(media => {
        if (!media.id) formData.append("media[]", media ? media : "''");
      });

      this.form.tags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag ? tag : "''");
      });
      this.form.categories.forEach((category, index) => {
        formData.append(`categories[${index}]`, category ? category : "''");
      });

      this.form.tourism_cities.forEach((city, index) => {
        formData.append(`tourism_cities[${index}]`, city);
      });

      for (const key in this.form) {
        if (typeof this.form[key] !== "object" && this.form[key] !== "")
          formData.append(key, this.form[key]);
      }
      !this.form.main_media["id"] &&
        formData.append("main_media", this.form.main_media);
      if (this.isEdit) {
        // in case edit
        formData.append("_method", "put");
        formData.append("locale", this.locale);
      }

      // start point  address
      for (const key in this.address.start_point_address) {
        formData.append(
          `start_point_address[${key}]`,
          this.address.start_point_address[key]
            ? this.address.start_point_address[key]
            : ""
        );
      }
      // end point  address
      for (const key in this.address.end_point_address) {
        formData.append(
          `end_point_address[${key}]`,
          this.address.end_point_address[key]
            ? this.address.end_point_address[key]
            : ""
        );
      }

      if (this.form.includes && this.form.includes.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.includes.forEach((include) => {
          formData.append(`includes[]`, JSON.stringify(include));
        });
      } else {
        formData.append(`includes`, "''");
      }
      if (this.form.excludes && this.form.excludes.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.excludes.forEach((exclude) => {
          formData.append(`excludes[]`, exclude);
        });
      } else {
        formData.append(`excludes`, "''");
      }
      if (this.form.needs && this.form.needs.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.needs.forEach((need) => {
          formData.append(`needs[]`, need);
        });
      } else {
        formData.append(`needs`, "''");
      }
      if (this.form.conditions && this.form.conditions.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.conditions.forEach((condition) => {
          formData.append(`conditions[]`, condition);
        });
      } else {
        formData.append(`conditions`, "''");
      }
      if (this.form.available_dates && this.form.available_dates.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.available_dates.forEach((available_date) => {
          formData.append(`available_dates[]`, `${available_date} 00:00:00`);
        });
      } else {
        formData.append(`available_dates`, "''");
      }

      return formData;
    },

    handleSubmit() {
      this.loadingButton = true;

      if (!this.isEdit) {
        // in case create
        StoreData({ reqName: "packages", data: this.generateData() })
          .then(() => {
            this.$router.push({ name: "packages" });
          })
          .finally(() => {
            this.loadingButton = false;
          });
      } else {
        UpdateData({
          reqName: "packages",
          data: this.generateData(),
          param: this.$route.params.slug,
          locale: this.locale
        })
          .then(() => {
            this.$router.push({ name: "packages" });
          })
          .finally(() => {
            this.loadingButton = false;
          });
      }
      // }
    },
    changeLocale(locale) {
      this.locale = locale;
      this.handleShowData(locale);
    },

    async onCountrySelect(country_id, state_type) {
      const res = await IndexData({
        reqName: `countries/${country_id}/states`,
        query: { pagination: "all" }
      });
      const { states } = res.data;
      this[state_type] = states;
    },
    async getCities(state_id, country, cityField) {
      const res = await IndexData({
        reqName: `countries/${country}/states/${state_id}/cities`,
        query: { pagination: "all" }
      });
      const { cities } = res.data;
      this[cityField] = cities;
    }
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(191)
      },
      sub_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(191)
      },
      label: {
        maxLength: maxLength(16)
      },
      duration_in_nights: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(90)
      },
      cancel_before_start_in_hours: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(336)
      },
      reserve_before_start_in_hours: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(336)
      },
      return_pct: {
        minValue: minValue(0),
        maxValue: maxValue(100)
      },
      description: {
        required,
        maxWords: maxWords(40000)
      },
      country: {
        required
      },
      main_media: {
        required
      },
      tourism_cities: {
        required
      }
    }
  },

  watch: {
    "address.start_point_address.country_id": {
      handler(value) {
        if (value) {
          this.onCountrySelect(value, "startPointStates");
        }
      },
      immediate: true
    },
    "address.end_point_address.country_id": {
      handler(value) {
        if (value) {
          this.onCountrySelect(value, "endPointState");
        }
      },
      immediate: true
    },
    "address.start_point_address.state_id": {
      handler(value) {
        if (value) {
          this.getCities(
            value,
            this.address.start_point_address.country_id,
            "startPointCites"
          );
        }
      },
      immediate: true
    },
    "address.end_point_address.state_id": {
      handler(value) {
        if (value) {
          this.getCities(
            value,
            this.address.end_point_address.country_id,
            "endPointCities"
          );
        }
      },
      immediate: true
    },

    isEdit: {
      handler(value) {
        if (value) {
          this.handleShowData();
        }
      },
      immediate: true
    }
  }
};
</script>
